import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button/Button.jsx";
import Panel from "../../components/ui/Panel/Panel.jsx";
import ConfirmModal from "../../components/ui/Modal/ConfirmModal.jsx";
import BusinessCard from "../../components/cards/BusinessCard/BusinessCard.jsx";
import { BUSINESS_TYPES } from "../../data/rates.js";
import { useApp } from "../../hooks/useApp.js";
import { listBusinesses, deleteBusiness } from "../../services/businessService.js";

const BUSINESS_TYPE_LABELS = Object.fromEntries(BUSINESS_TYPES);
const PAGE_SIZE = 10;

const Businesses = () => {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let cancelled = false;
    listBusinesses()
      .then((list) => {
        if (!cancelled) setBusinesses(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addBusiness = () => {
    dispatch({ type: "SEED", which: "new" });
    navigate("/dashboard/businesses/form");
  };

  const editBusiness = (business) => {
    dispatch({ type: "SEED", which: "new" });
    dispatch({ type: "SET_BIZ", patch: business });
    dispatch({ type: "SET_BUSINESS_ID", businessId: business.id });
    navigate("/dashboard/businesses/form");
  };

  // "Continue filing" on an already-saved business - not a blank "New
  // Filing" - so it goes straight into the wizard (same as Dashboard.jsx's
  // BusinessCards / MyFilings.jsx), landing on Form with its saved values.
  // Bumping maxStep here (same GO_STEP high-water-mark trick MyFilings.jsx
  // uses) keeps the stepper visible from the first click instead of the
  // bare landing treatment reserved for a truly fresh filing.
  const continueFiling = (business) => {
    dispatch({ type: "SEED", which: "new" });
    dispatch({ type: "SET_BIZ", patch: business });
    dispatch({ type: "SET_BUSINESS_ID", businessId: business.id });
    dispatch({ type: "GO_STEP", step: 2 });
    navigate("/wizard/1");
  };

  const removeBusiness = (business) => {
    setDeleteError("");
    setPendingDelete(business);
  };

  const cancelDelete = () => {
    setPendingDelete(null);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    const business = pendingDelete;
    if (!business) return;
    setDeletingId(business.id);
    try {
      await deleteBusiness(business.id);
      setBusinesses((prev) => prev.filter((b) => b.id !== business.id));
      setPage((p) => {
        const remaining = businesses.length - 1;
        const maxPage = Math.max(0, Math.ceil(remaining / PAGE_SIZE) - 1);
        return Math.min(p, maxPage);
      });
      setPendingDelete(null);
      setDeleteError("");
    } catch (err) {
      setDeleteError(err.message || "Couldn't delete this business. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="flex justify-between items-end mb-5 gap-4 flex-wrap">
        <div>
          <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
            Accounts
          </div>
          <h1 className="text-[28px] leading-[1.1] mt-1">Businesses</h1>
          <p className="text-[var(--muted)] max-w-[62ch]">
            Every business registered on your account.
          </p>
        </div>
        <Button size="lg" onClick={addBusiness}>
          + Add Business
        </Button>
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))" }}>
        {!loading && businesses.length === 0 && (
          <Panel padded className="text-center py-10 col-span-full">
            <h2 className="text-xl font-semibold mb-1">No businesses yet</h2>
            <p className="text-[var(--muted)] max-w-[46ch] mx-auto mb-4">
              Add a business to start filing your Form 2290.
            </p>
            <Button size="lg" onClick={addBusiness}>
              Add Business
            </Button>
          </Panel>
        )}

        {businesses.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE).map((b) => (
          <BusinessCard
            key={b.id}
            business={b}
            businessTypeLabel={BUSINESS_TYPE_LABELS[b.businessType]}
            onContinue={() => continueFiling(b)}
            onEdit={() => editBusiness(b)}
            onDelete={deletingId === b.id ? undefined : () => removeBusiness(b)}
          />
        ))}
      </div>

      <ConfirmModal
        open={!!pendingDelete}
        title="Delete Confirmation"
        confirmLabel="Delete"
        loading={deletingId === pendingDelete?.id}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>

      {businesses.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 mt-5">
          <div className="text-[13px] text-[var(--muted)]">
            Page {page + 1} of {Math.ceil(businesses.length / PAGE_SIZE)}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="!text-xs !px-3 !py-1.5"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="!text-xs !px-3 !py-1.5"
              onClick={() => setPage((p) => Math.min(Math.ceil(businesses.length / PAGE_SIZE) - 1, p + 1))}
              disabled={page >= Math.ceil(businesses.length / PAGE_SIZE) - 1}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Businesses;
